import { useMemo } from 'react';

import { type AudienceOptionType, useAudienceOptionById, useAudienceOptions } from '@/api/audience';
import { useCurrentInstance } from '@/api/organization';

type AudienceResolutionOptions = {
  urlAudienceId?: string;
  defaultAudience?: AudienceOptionType;
  allOptions?: AudienceOptionType[];
  prependedOptions?: AudienceOptionType[];
};

const useResolvedAudience = ({
  urlAudienceId,
  defaultAudience,
  prependedOptions,
}: AudienceResolutionOptions) => {
  const {
    audienceOptions,
    isLoading: isLoadingAudienceOptions,
    error: errorAudienceOptions,
  } = useAudienceOptions({ prependedOptions });
  const { instance } = useCurrentInstance();
  const tamAudience = instance?.tam?.audience;

  const audienceInOptions = useMemo(() => {
    return (prependedOptions ?? [])
      .concat(audienceOptions ?? [])
      .find((opt) => opt.id === urlAudienceId);
  }, [audienceOptions, prependedOptions, urlAudienceId]);

  // Fetch audience by ID if we have an ID but no name
  const needsFetch = urlAudienceId && !audienceInOptions;

  const {
    audienceOption: fetchedAudienceOption,
    isLoading: isFetchingAudienceOption,
    error: errorFetchingAudienceOption,
  } = useAudienceOptionById(needsFetch ? urlAudienceId : undefined);

  const resolvedAudience = useMemo(() => {
    // Priority 1: URL parameter (explicit user choice or page navigation)
    if (urlAudienceId) {
      // Check if it's in options first
      if (audienceInOptions) {
        return audienceInOptions;
      }

      if (fetchedAudienceOption) {
        return fetchedAudienceOption;
      }

      // If we're still fetching, return a placeholder
      if (isFetchingAudienceOption) {
        return { id: urlAudienceId, name: '' };
      }

      // If fetch failed or audience doesn't exist
      return undefined;
    }

    // Priority 2: Default audience prop (page explicitly sets initial audience)
    if (defaultAudience) {
      return defaultAudience;
    }

    // Priority 3: TAM as an audience
    if (tamAudience) {
      return tamAudience;
    }

    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    urlAudienceId,
    prependedOptions,
    fetchedAudienceOption,
    isFetchingAudienceOption,
    defaultAudience?.id,
    tamAudience?.id,
    audienceInOptions,
  ]);

  return {
    audience: resolvedAudience,
    isLoading: isFetchingAudienceOption || isLoadingAudienceOptions,
    error: errorAudienceOptions || errorFetchingAudienceOption,
  };
};

export default useResolvedAudience;
